import React, { useEffect, useState } from 'react';
import {
    Box,
    Grid,
    Button,
    Typography,
    SvgIcon,
    TableContainer,
    Table,
    TableBody,
    TableRow,
    TableCell,
    Paper,
    Card,
    Stack,
    CircularProgress
} from '@mui/material';
import { RefreshRounded } from '@mui/icons-material';
import BalanceLoader from '../GlobalComponents/BalanceLoader';
import axios from 'axios';
import { getAxiosConfig } from '../../../helpers/AuthHelper';
import { BalanceData } from '../Payments/PaymentTypes';

type BalanceItem = {
    available: number;
    pending: number;
    total: number;
};

const coinColorMap: { [key: string]: string } = {
    BTC: '#f7931a',
    ETH: '#3c3c3d',
    ALPH: '#ff3e3e',
    LTC: '#bebebe',
    USDT: '#26a17b',
    DOGE: '#c2a633',
    KAS: '#6deed4',
};

const BalanceDisplay = () => {
    const [balance, setBalance] = useState<BalanceData | null>(null);
    const [balanceLoading, setBalanceLoading] = useState<boolean>(true);

    useEffect(() => {
        fetchBalance();
    }, []);

    const fetchBalance = () => {
        setBalanceLoading(true);
        axios
            .get('/api/segPoolRewardDistribution/balance', getAxiosConfig())
            .then((response) => {
                if (isObjectOfBalance(response.data)) {
                    setBalance(response.data);
                }
                setBalanceLoading(false);
            })
            .catch((error) => {
                console.error('Error fetching balance:', error);
                setBalanceLoading(false);
            });
    };

    function isObjectOfBalance(obj: any): obj is BalanceData {
        if (typeof obj !== 'object' || obj === null) return false;
        for (const key in obj) {
            const value = obj[key];
            if (
                typeof value !== 'object' ||
                typeof value.available !== 'number' ||
                typeof value.pending !== 'number' ||
                typeof value.total !== 'number'
            ) {
                return false;
            }
        }
        return true;
    }

    return (
        <Box sx={{ width: '100%', padding: '16px' }}>
            <Stack direction="row" alignItems="center" justifyContent="start" gap={2} sx={{ mb: 2, mx: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#222' }}>
                    Hot Wallet
                </Typography>
                <Button
                    onClick={fetchBalance}
                    variant="outlined"
                    size="small"
                    startIcon={<RefreshRounded />}
                    sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 500,
                        borderColor: '#ccc',
                        color: '#555',
                        '&:hover': {
                            backgroundColor: '#f5f5f5',
                            borderColor: '#bbb',
                        },
                    }}
                >
                    Refresh
                </Button>
            </Stack>

            <Grid container spacing={2} alignItems="center" justifyContent="start">
                {balanceLoading ? (
                    <Grid item xs={11}>
                        <BalanceLoader />
                    </Grid>
                ) : balance && typeof balance === 'object' && !Array.isArray(balance) ? (
                    Object.entries(balance).map(([coin, data]) => (
                        <Grid item xs={12} sm={3} key={coin}>
                            <Card
                                sx={{
                                    p: 2,
                                    borderRadius: '16px',
                                    backgroundColor: '#fff',
                                    border: '1px solid #e0e0e0',
                                    boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
                                    transition: 'transform 0.2s ease',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: '0 6px 14px rgba(0,0,0,0.08)',
                                    },
                                }}
                            >
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Box sx={{ flex: 1 }}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                width: '100%',
                                            }}
                                        >
                                            <Typography
                                                variant="subtitle1"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: '#333',
                                                    ml: 2,
                                                }}
                                            >
                                                {coin}
                                            </Typography>

                                            <Box
                                                sx={{
                                                    width: 30,
                                                    height: 30,
                                                    borderRadius: '50%',
                                                    backgroundColor: coinColorMap[coin] || '#888',
                                                    color: '#fff',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '1rem',
                                                    fontWeight: 'bold',
                                                    textTransform: 'uppercase',
                                                    mb: 2
                                                }}
                                            >
                                                {coin[0]}
                                            </Box>
                                        </Box>


                                        <TableContainer
                                            component={Paper}
                                            elevation={0}
                                            sx={{ boxShadow: 'none', backgroundColor: 'transparent' }}
                                        >
                                            <Table size="small">
                                                <TableBody>
                                                    <TableRow>
                                                        <TableCell
                                                            sx={{
                                                                border: 'none',
                                                                padding: '4px 8px',
                                                                color: '#666',
                                                                fontSize: '0.85rem',
                                                            }}
                                                        >
                                                            Available
                                                        </TableCell>
                                                        <TableCell
                                                            align="right"
                                                            sx={{
                                                                border: 'none',
                                                                padding: '4px 8px',
                                                                color: '#1977d3',
                                                                fontWeight: 'bold',
                                                                fontSize: '0.85rem',
                                                            }}
                                                        >
                                                            {data.available.toFixed(8)}
                                                        </TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableCell
                                                            sx={{
                                                                border: 'none',
                                                                padding: '4px 8px',
                                                                color: '#666',
                                                                fontSize: '0.85rem',
                                                            }}
                                                        >
                                                            Pending
                                                        </TableCell>
                                                        <TableCell
                                                            align="right"
                                                            sx={{
                                                                border: 'none',
                                                                padding: '4px 8px',
                                                                color: '#1977d3',
                                                                fontWeight: 'bold',
                                                                fontSize: '0.85rem',
                                                            }}
                                                        >
                                                            {data.pending.toFixed(8)}
                                                        </TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableCell
                                                            sx={{
                                                                border: 'none',
                                                                padding: '4px 8px',
                                                                color: '#666',
                                                                fontSize: '0.85rem',
                                                            }}
                                                        >
                                                            Total
                                                        </TableCell>
                                                        <TableCell
                                                            align="right"
                                                            sx={{
                                                                border: 'none',
                                                                padding: '4px 8px',
                                                                color: '#43a047',
                                                                fontWeight: 'bold',
                                                                fontSize: '0.85rem',
                                                            }}
                                                        >
                                                            {data.total.toFixed(8)}
                                                        </TableCell>
                                                    </TableRow>
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Box>
                                </Stack>
                            </Card>
                        </Grid>
                    ))
                ) : (
                    <Grid item xs={12}>
                        <Typography>No balance data available</Typography>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export default BalanceDisplay;
